<template>
  <figure
    :class="['dn-video', className, siteName]"
    itemscope
    itemtype="http://schema.org/ImageObject"
  >
    <div
      :id="videoContainerId"
      class="dn-video__jwplayer"
    />
  </figure>
</template>
<script lang="ts" setup>
import { onMounted } from 'vue';
import { type PublicationContext, type WindowIncludingLoadedScript } from '~/types';

type WindowIncludingJwplayer = Window & typeof globalThis & { jwplayer: any };

const props = defineProps({
  videoContainerId: {
    type: String,
    default () {
      return `videoPlayer-${Math.floor(Math.random() * 1000000)}`;
    }
  },
  className: {
    type: String,
    default: ''
  },
  videoId: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  duration: {
    type: String,
    default: ''
  },
  publishedAt: {
    type: String,
    default: ''
  },
  autoPlay: {
    type: Boolean,
    default: false
  },
  siteName: {
    type: String,
    default: 'dn_horizontal'
  }
});

const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;
const windowWithLoadedScript = window as WindowIncludingLoadedScript;
const isMounted = ref(false);
const isRendered = ref(false);

const orientation = computed(() => (props.siteName === 'dn_vertical' ? 'vertical' : 'horizontal'));

/*
 * Mirrors the variant resolution in plugins/jwplayer.client.ts: autoplay
 * videos get a dedicated JW player build, and publications without one
 * fall back to the standard build. Both sides must derive the same key.
 */
const jwplayerScriptKey = computed(() => {
  const variant = props.autoPlay && rtConfPub.jwplayer?.[`${orientation.value}AutoplaySrc`] ? `${orientation.value}Autoplay` : orientation.value;

  return `jwplayer${variant.charAt(0).toUpperCase()}${variant.slice(1)}`;
});

onMounted(() => {
  isMounted.value = true;
  if (!isRendered.value) {
    renderJWVideoPlayer();
  }
});

// Watch the specific jwplayer script based on siteName
watch(
  () => windowWithLoadedScript.loadedScripts?.[jwplayerScriptKey.value],
  (newValue) => {
    if (newValue && isMounted.value && !isRendered.value) {
      renderJWVideoPlayer();
    }
  },
  { immediate: true, deep: true }
);

const renderJWVideoPlayer = () => {
  const scriptLoaded = windowWithLoadedScript.loadedScripts?.[jwplayerScriptKey.value]?.value;
  if (scriptLoaded && typeof (window as WindowIncludingJwplayer).jwplayer !== 'undefined') {
    const topVideoPlayer = (window as WindowIncludingJwplayer).jwplayer(`${props.videoContainerId}`);
    /*
     * Autoplay (and its required mute) is baked into the dedicated JW player
     * builds selected via jwplayerScriptKey, so setup() must not override it.
     *
     * The aspect ratio, however, has to be explicit. Every build assigns the
     * same window.jwplayer global, so on an article that mixes orientations
     * the library that finishes loading last wins the global and its default
     * ratio would be applied to every player set up after that point — a
     * horizontal video rendering at 9:16 (padding-top: 177.778%) or the
     * reverse, depending on which script won the race.
     */
    topVideoPlayer.setup({
      playlist: `//cdn.jwplayer.com/v2/media/${props.videoId}`,
      aspectratio: orientation.value === 'vertical' ? '9:16' : '16:9'
    });
    topVideoPlayer.on('ready', () => {
      const jwPlayerLoadedEvent = new Event('jwplayer:loaded');
      window.dispatchEvent(jwPlayerLoadedEvent);
    });
    isRendered.value = true;
  }
};
</script>
<style lang="scss">
.dn-video {
  z-index: inherit;

  .jw-reset:not(.jw-logo) {
    border-radius: var(--box-radius);
  }

  .jw-logo {
    max-width: 32px;

    @media only screen and (width >= 768px) {
      max-width: 42px;
    }
  }

  .jw-title {
    position: absolute;
    bottom: 0;
    top: auto;
    font-family: var(--typography-utility-m-bold-font-family);
    font-size: var(--typography-utility-m-bold-font-size);
    line-height: var(--typography-utility-m-line-height);
    padding-bottom: var(--row-gap);

    @media only screen and (width >= 768px) {
      font-size: var(--typography-utility-l-bold-font-size);
      line-height: var(--typography-utility-l-bold-line-height);
    }

    &-primary,
    &-secondary {
      font-family: var(--typography-utility-m-bold-font-family) !important;
      font-size: var(--typography-utility-m-bold-font-size) !important;
      line-height: var(--typography-utility-m-line-height) !important;
      font-weight: 500;
      text-overflow: unset;
      white-space: normal;

      @media only screen and (width >= 768px) {
        font-size: var(--typography-utility-l-bold-font-size) !important;
        line-height: var(--typography-utility-l-bold-line-height);
      }
    }

    &-primary {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 var(--row-gap);
      padding-right: 4rem;
    }
  }

  .jw-controls-backdrop {
    background: linear-gradient(to bottom, rgb(12 18 23 / 0%) 50%, rgb(12 18 23 / 50%) 100%);
  }

  .jw-state-idle {
    .jw-controls {
      background: none;
    }
  }

  .jw-display {
    .jw-display-icon-container {
      .jw-icon {
        width: 60px;
        height: 60px;
        border: 2px solid #fff;
        border-radius: 100%;

        .jw-svg-icon-play {
          line-height: 32px;
          width: 32px;
          height: 32px;
          padding: 13px;

          @media only screen and (width >= 768px) {
            line-height: 42px;
            width: 42px;
            height: 42px;
          }
        }
      }
    }
  }

  .jw-video-thumbnail-container {
    .jw-video-thumbnail {
      background: transparent;
    }
  }

  @media only screen and (width >= 768px) {
    &.dn_vertical {
      border-radius: var(--box-radius);

      .jwplayer {
        border-radius: 0;

        &:not(.jw-flag-fullscreen) {
          max-width: 400px;
          margin: 0 auto;
        }
      }
    }
  }
}
</style>
